import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, AlertTriangle, Clock } from 'lucide-react';

const NOTICE_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600', ready_to_issue: 'bg-blue-100 text-blue-700',
  issued: 'bg-purple-100 text-purple-700', served: 'bg-green-100 text-green-700',
  response_due: 'bg-amber-100 text-amber-700', responded: 'bg-teal-100 text-teal-700',
  closed: 'bg-slate-100 text-slate-600', time_bar_risk: 'bg-red-100 text-red-700',
};

export default function DisputeNoticesTab({ projectId, settings }) {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const regRes = await api.disputes.getRegister(projectId, {});
        const matters = regRes.data.items || [];
        const allNotices = [];
        for (const m of matters.slice(0, 20)) {
          try {
            const nRes = await api.disputes.listNotices(m.id);
            const ns = nRes.data || [];
            ns.forEach(n => { n.matter_ref = m.matter_ref; n.matter_title = m.title; });
            allNotices.push(...ns);
          } catch { /* skip */ }
        }
        setNotices(allNotices.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')));
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [projectId]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const timeBars = notices.filter(n => n.status === 'time_bar_risk');
  const drafts = notices.filter(n => n.status === 'draft' || n.status === 'ready_to_issue');
  const active = notices.filter(n => ['issued', 'served', 'response_due'].includes(n.status));

  return (
    <div className="space-y-4" data-testid="notices-tab">
      <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">All Notices ({notices.length})</h2>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{notices.length}</p><p className="text-[10px] text-slate-500 uppercase">Total</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-slate-600">{drafts.length}</p><p className="text-[10px] text-slate-500 uppercase">Drafts</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-600">{active.length}</p><p className="text-[10px] text-slate-500 uppercase">Active</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-red-600">{timeBars.length}</p><p className="text-[10px] text-slate-500 uppercase">Time-Bar Risk</p></CardContent></Card>
      </div>

      {timeBars.length > 0 && (
        <Card className="border-red-200"><CardContent className="p-4">
          <h3 className="text-xs font-bold uppercase text-red-600 mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Time-Bar Risk Notices</h3>
          {timeBars.map(n => (
            <div key={n.id} className="flex items-center gap-3 p-2 border-b last:border-b-0">
              <span className="font-mono text-xs text-slate-400">{n.notice_ref}</span>
              <span className="text-sm flex-1">{n.notice_type?.replace(/_/g, ' ')}</span>
              <span className="text-xs text-slate-500">{n.matter_ref}</span>
              <Badge className="bg-red-100 text-red-700 text-[10px]">Time-Bar Risk</Badge>
            </div>
          ))}
        </CardContent></Card>
      )}

      {notices.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-500">No notices across any disputes</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {notices.map(n => (
            <Card key={n.id}>
              <CardContent className="p-3 flex items-center gap-3">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{n.notice_ref}</span>
                    <span className="text-sm font-medium capitalize">{n.notice_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                    <span>{n.matter_ref} — {n.matter_title}</span>
                    {n.required_by_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{n.required_by_date}</span>}
                  </div>
                </div>
                <Badge className={`text-[10px] ${NOTICE_STATUS_COLORS[n.status]}`}>{n.status?.replace(/_/g, ' ')}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
